import React, { useContext, useEffect, useState } from 'react'
import { TouchableOpacity, View } from 'react-native'

import PropTypes from 'prop-types'

import { Icon, VFText } from '@vfgroup-oneplatform/foundation/Components'
import RecommendedBadge from '@vfgroup-oneplatform/framework/CommonUI/RecommendedBadge'

import { useTheme } from '@vfgroup-oneplatform/foundation/Components/Themes'

import UnlimitedContext from '../../Context'

import { BUNDLE_TYPE } from '../../Utils/bundle'

import Offer from '../Offer'
import CustomErrorScreen from '../CustomErrorScreen'
import CoverageScreen from '../../Screens/CoverageScreen'
import CustomBundleConfigurator from '../CustomBundleConfigurator'

import styles from './OfferStep.Styles'
import UhImages from '../../Themes/Images'



const OfferStep = ({
  goNext,
  scrollToTop,
  selectBundle,
  getATLBundles,
  getFixAndTVs,
  getTariffPlans,
  getMemberSlots,
  getPegaBundles,
  getCoveredAreas,
  changeBundleType,
  validateLocation,
  resetCoverageFlag,
  updateCustomBundle,
  resetLocationState,
  resetCoverageError,
  getCustomBundlePrice,
  checkCustomerByMSISDN,
  resetCustomerCheckError,
  updateCity
}) => {
  const {
    data: {
      // General
      mapAPIKey,
      // Bundle
      atlBundles,
      bundleType,
      pegaBundles,
      // Location
      locationData,
      // Customer
      customerData
    },
  } = useContext(UnlimitedContext)

  const [map, setMap] = useState({
    type: null,
    show: false,
    bundleId: ''
  })

  const [coveragePolygons, setCoveragePolygons] = useState({
    isLoading: false,
    data: []
  })

  useEffect(() => {
    if (
      bundleType !== BUNDLE_TYPE.custom &&
      selectBundle !== null &&
      bundleType !== null
    ) {
      // Resetting the selected bundle and bundle type
      selectBundle(null)
      changeBundleType(null)
    }
    // Resetting the location state
    resetLocationState()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleNextClick = () => {
    if (bundleType !== BUNDLE_TYPE.custom) {
      selectBundle(map.bundleId)
      changeBundleType(map.type)
    }
    checkCustomerByMSISDN({
      onError: () => { },
      onStart: () => { },
      onSuccess: () => goNext()
    })
  }

  const openCoverageCheck = (bundleId, type) => {
    setMap({ show: true, bundleId, type })
  }

  const handleLocationChange = data => {
    // if (data.city !== locationData.city) {
    getCoveragePolygons(data.city)
    validateLocation({ body: data })
    // }
  }

  const handleModalToggle = () => {
    resetCustomerCheckError()
    resetCoverageError()
    resetCoverageFlag()
    resetLocationState()
    // Resetting selected offer when closing the dialog
    setMap({ type: null, show: false, bundleId: '' })
  }

  const toggleIsCustomBundle = isCustom => {
    if (isCustom) {
      changeBundleType(BUNDLE_TYPE.custom)
    } else {
      changeBundleType(null)
    }
    scrollToTop()
  }

  const handleCoverageClose = () => resetCoverageFlag()

  useEffect(() => {
    Promise.all([getPega(), getATL()])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getCoveragePolygons = city => {
    getCoveredAreas({
      body: { city },
      onStart: () =>
        setCoveragePolygons(prev => ({ ...prev, isLoading: true })),
      onError: () =>
        setCoveragePolygons(prev => ({ ...prev, isLoading: false })),
      onSuccess: data => setCoveragePolygons({ isLoading: false, data })
    })
  }

  const showPegaOffers =
    pegaBundles.isLoading ||
    pegaBundles.isError ||
    (Array.isArray(pegaBundles.data) && pegaBundles.data.length)
  const showATLOffers =
    atlBundles.isLoading ||
    atlBundles.isError ||
    (Array.isArray(atlBundles.data) && atlBundles.data.length)

  const getPega = () => getPegaBundles({})
  const getATL = () => getATLBundles({})
  const theme = useTheme()

  const hasCoverage = coveragePolygons.isLoading
    ? null
    : locationData.hasCoverage

  const handleCoverageErrorClose = () =>
    customerData.isError ? resetCustomerCheckError() : resetCoverageError()

  const isCheckingCoverage =
    locationData.isLoading ||
    customerData.isLoading ||
    coveragePolygons.isLoading

  return (
    <View style={styles.offerStep}>
      {bundleType === BUNDLE_TYPE.custom ? (
        <CustomBundleConfigurator
          goNext={openCoverageCheck}
          getFixAndTVs={getFixAndTVs}
          getTariffPlans={getTariffPlans}
          getMemberSlots={getMemberSlots}
          getPrice={getCustomBundlePrice}
          updateCustomBundle={updateCustomBundle}
          toggleIsCustomBundle={toggleIsCustomBundle}
        />
      ) : (
        <View>
          {showPegaOffers ? (
            <View>
              <VFText style={styles.header} i18nKey="unlimited_offer_title" />
              <VFText
                style={styles.subHeader(theme)}
                i18nKey="unlimited_offer_subtitle"
              />
              <OffersList
                recommended
                onRetry={getPega}
                offers={pegaBundles.data}
                isError={pegaBundles.isError}
                isLoading={pegaBundles.isLoading}
                onSelect={data => openCoverageCheck(data, BUNDLE_TYPE.pega)}
              />
            </View>
          ) : null}
          {showATLOffers ? (
            <View>
              <VFText
                style={styles.header}
                i18nKey="unlimited_standard_offers"
              />
              <OffersList
                count={2}
                onRetry={getATL}
                offers={atlBundles.data}
                isError={atlBundles.isError}
                isLoading={atlBundles.isLoading}
                onSelect={data => openCoverageCheck(data, BUNDLE_TYPE.standard)}
              />
            </View>
          ) : null}
          <ConfigureOffer
            onPress={() => toggleIsCustomBundle(true)}
            isLoading={pegaBundles.isLoading || atlBundles.isLoading}
          />
        </View>
      )}
      <CoverageScreen
        show={map.show}
        apiKey={mapAPIKey}
        onNext={handleNextClick}
        hasCoverage={hasCoverage}
        city={locationData.street}
        street={locationData.city}
        onClose={handleModalToggle}
        buttonText="unlimited_continue"
        polygons={coveragePolygons.data}
        title="unlimited_coverage_title"
        latitude={locationData.latitude}
        longitude={locationData.longitude}
        subtitle="unlimited_coverage_subtitle"
        isCheckingCoverage={isCheckingCoverage}
        onLocationChange={handleLocationChange}
        onNoCoverageClose={handleCoverageClose}
        onCoverageErrorClose={handleCoverageErrorClose}
        onMapReady={() => getCoveragePolygons(locationData.city)}
        getCoveragePolygons={getCoveragePolygons}
        isCoverageError={locationData.isError || customerData.isError}
        coverageErrorMessage={
          customerData.isError ? 'unlimited_customer_check_error' : undefined
        }
        setMap={setMap}
        updateCity={updateCity}
        validateLocation={validateLocation}
      />
    </View>
  )
}

const OffersList = ({
  count,
  offers,
  isError,
  onRetry,
  onSelect,
  isLoading,
  recommended
}) => {
  const handleOfferSelection = id => onSelect(id)
  const theme = useTheme()
  const offersArr = isLoading
    ? Array(count)
      .fill(1)
      .map((_, i) => {
        return { recommended: i === 0 ? recommended : false, price: 1 }
      })
    : offers

  if (isError) {
    return (
      <View style={styles.error}>
        <CustomErrorScreen onPress={onRetry} />
      </View>
    )
  }

  return (
    <View>
      {offersArr.map(offer =>
        offer.recommended ? (
          <RecommendedOffer
            offer={offer}
            isLoading={isLoading}
            onPress={handleOfferSelection}
          />
        ) : (
          <Offer
            type="bundle"
            offer={offer}
            isLoading={isLoading}
            onPress={handleOfferSelection}
            stylesArray={[styles.collapsibleCard(theme)]}
          />
        )
      )}
    </View>
  )
}

const RecommendedOffer = ({ offer, onPress, isLoading }) => {
  const theme = useTheme()
  return (
    <View>
      <RecommendedBadge
        i18nKey="unlimited_home_recommended"
        containerStyle={styles.recommendedBadge}
      />
      <Offer
        type="bundle"
        offer={offer}
        onPress={onPress}
        isLoading={isLoading}
        stylesArray={[styles.collapsibleCard(theme), styles.recommendedBorder]}
      />
    </View>
  )
}

const ConfigureOffer = ({ onPress, isLoading }) => {
  return (
    <View style={styles.card}>
      <View style={styles.configHeader}>
        <Icon
          size={26}
          type="image"
          style={styles.white}
          name={UhImages.custom_bundle}
        />
        <VFText
          i18nKey="unlimited_create_plan"
          style={[styles.configTitle, styles.white]}
        />
      </View>
      <VFText
        i18nKey="unlimited_create_description"
        style={[styles.configDescription, styles.white]}
      />
      <TouchableOpacity
        activeOpacity={0.75}
        style={styles.configBtn}
        onPress={() => {
          if (!isLoading) {
            onPress()
          }
        }}
      >
        <VFText i18nKey="unlimited_create_plan" style={styles.configTxt} />
      </TouchableOpacity>
    </View>
  )
}

OfferStep.propTypes = {
  goNext: PropTypes.func,
  scrollToTop: PropTypes.func,
  selectBundle: PropTypes.func,
  getFixAndTVs: PropTypes.func,
  getATLBundles: PropTypes.func,
  getTariffPlans: PropTypes.func,
  getMemberSlots: PropTypes.func,
  getPegaBundles: PropTypes.func,
  getCoveredAreas: PropTypes.func,
  changeBundleType: PropTypes.func,
  validateLocation: PropTypes.func,
  resetCoverageFlag: PropTypes.func,
  resetLocationState: PropTypes.func,
  updateCustomBundle: PropTypes.func,
  resetCoverageError: PropTypes.func,
  getCustomBundlePrice: PropTypes.func,
  checkCustomerByMSISDN: PropTypes.func,
  resetCustomerCheckError: PropTypes.func
}

OffersList.propTypes = {
  isError: PropTypes.bool,
  count: PropTypes.number,
  offers: PropTypes.array,
  onRetry: PropTypes.func,
  onSelect: PropTypes.func,
  isLoading: PropTypes.bool,
  recommended: PropTypes.bool
}

RecommendedOffer.propTypes = {
  offer: PropTypes.object,
  onPress: PropTypes.func,
  isLoading: PropTypes.func
}

ConfigureOffer.propTypes = {
  onPress: PropTypes.func,
  isLoading: PropTypes.bool
}

export default OfferStep
